import StaticPageLayout from '../../components/StaticPageLayout';
import { useThemeStore } from '../../store/theme';

export default function RefundPolicy() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  const headingClass = isDark ? 'text-white' : 'text-slate-900';
  const subheadingClass = isDark ? 'text-white' : 'text-slate-800';
  const textClass = isDark ? 'text-gray-300' : 'text-slate-600';

  return (
    <StaticPageLayout
      title="Refund Policy"
      subtitle="Last updated: January 26, 2026"
    >
      <div className={`prose prose-lg max-w-none ${isDark ? 'prose-invert' : ''}`}>
        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>1. 14-Day Free Trial</h2>
          <p className={`${textClass} mb-4`}>
            SweetPilot offers a 14-day free trial for all new users. During the trial period, you can explore all features of our AI employee platform without any charge.
          </p>
          <ul className={`list-disc pl-6 ${textClass} space-y-2`}>
            <li>The trial begins when you create your account</li>
            <li>You will receive complimentary credits to test our AI employees</li>
            <li>No credit card is required to start your trial</li>
            <li>You can upgrade to a paid plan at any time during the trial</li>
            <li>If you do not upgrade, your account will be limited after the trial ends</li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>2. Credit-Based Refund Policy</h2>
          <p className={`${textClass} mb-4`}>
            Our refund policy is based on your remaining unused credits. We believe in fair and transparent refunds that reflect the actual value you've received from our service.
          </p>

          <h3 className={`text-xl font-semibold ${subheadingClass} mb-3`}>2.1 How Refunds Work</h3>
          <p className={`${textClass} mb-4`}>
            If you request a refund, we will calculate your refund amount based on the following formula:
          </p>
          <div className={`p-4 rounded-lg mb-4 ${isDark ? 'bg-white/5' : 'bg-slate-100'}`}>
            <p className={`${textClass} font-mono text-center`}>
              Refund Amount = (Remaining Credits / Total Credits Purchased) × Amount Paid
            </p>
          </div>
          <p className={textClass}>
            For example, if you purchased 1,000 credits for $50 and have 600 credits remaining, your refund would be $30.
          </p>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>3. Eligibility for Refunds</h2>
          <p className={`${textClass} mb-4`}>You are eligible for a refund if:</p>
          <ul className={`list-disc pl-6 ${textClass} space-y-2`}>
            <li>You have unused credits remaining in your account</li>
            <li>Your account is in good standing and not suspended</li>
            <li>You have not violated our Terms of Service</li>
            <li>The refund request is made within 90 days of the original purchase</li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>4. Non-Refundable Items</h2>
          <p className={`${textClass} mb-4`}>The following are not eligible for refunds:</p>
          <ul className={`list-disc pl-6 ${textClass} space-y-2`}>
            <li>Credits that have already been used</li>
            <li>Bonus or promotional credits received for free</li>
            <li>Gift card purchases (these are non-refundable but transferable)</li>
            <li>Purchases made more than 90 days ago</li>
            <li>Accounts terminated due to Terms of Service violations</li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>5. How to Request a Refund</h2>
          <p className={`${textClass} mb-4`}>
            To request a refund, please follow these steps:
          </p>
          <ol className={`list-decimal pl-6 ${textClass} space-y-2`}>
            <li>Log in to your SweetPilot account</li>
            <li>Navigate to Settings → Billing</li>
            <li>Click on "Request Refund" or contact our support team</li>
            <li>Provide your reason for the refund request</li>
            <li>Our team will review your request within 3-5 business days</li>
          </ol>
          <p className={`${textClass} mt-4`}>
            Alternatively, you can email us at <strong>support@sweetpilot.com</strong> with your refund request.
          </p>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>6. Refund Processing</h2>
          <p className={`${textClass} mb-4`}>
            Once your refund is approved:
          </p>
          <ul className={`list-disc pl-6 ${textClass} space-y-2`}>
            <li>Refunds will be processed within 5-10 business days</li>
            <li>The refund will be issued to the original payment method</li>
            <li>You will receive an email confirmation when the refund is processed</li>
            <li>Your remaining credits will be removed from your account</li>
            <li>Bank processing times may vary; please allow up to 7 additional days for the funds to appear</li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>7. Subscription Cancellation</h2>
          <p className={`${textClass} mb-4`}>
            If you have an active subscription:
          </p>
          <ul className={`list-disc pl-6 ${textClass} space-y-2`}>
            <li>You can cancel your subscription at any time from your account settings</li>
            <li>Your subscription benefits will continue until the end of your current billing period</li>
            <li>No partial refunds are provided for unused days within a billing period</li>
            <li>Remaining credits from credit purchases (not subscription benefits) may still be eligible for refund</li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>8. Disputes and Chargebacks</h2>
          <p className={textClass}>
            We encourage you to contact us directly before initiating a chargeback with your payment provider. Chargebacks incur fees and may result in account suspension. We are committed to resolving any issues fairly and promptly. Please reach out to our support team, and we will work with you to find a satisfactory resolution.
          </p>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>9. Changes to This Policy</h2>
          <p className={textClass}>
            We reserve the right to modify this Refund Policy at any time. Changes will be effective immediately upon posting to our website. Your continued use of our services after any changes indicates your acceptance of the updated policy. We will notify users of significant changes via email or through our platform.
          </p>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>10. Contact Us</h2>
          <p className={textClass}>
            If you have any questions about this Refund Policy or need assistance with a refund, please contact us:
          </p>
          <ul className={`list-none mt-4 ${textClass} space-y-2`}>
            <li><strong>Email:</strong> support@sweetpilot.com</li>
            <li><strong>Address:</strong> SweetPilot Inc., San Francisco, CA, United States</li>
            <li><strong>Support Hours:</strong> Monday - Friday, 9:00 AM - 6:00 PM PST</li>
          </ul>
        </section>
      </div>
    </StaticPageLayout>
  );
}
